import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';

import CheckBox from '../../../../../components/CheckBox/CheckBox';
import {
  selectIsLoadingUserHistory,
  selectIsProcessingUrbanYouthCertified,
  selectIsProcessingWotcEligibility,
} from '../../../../../store/selectors/userListSelector';
import { BLACK, FEED_BACK } from '../../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../../theme/fontConstants';
import { updateUrbanYouthCertified, updateWotcEligibility } from '../../../usersListReducer';

import SectionCard from './SectionCard';

const styles = {
  checkBoxLabel: {
    color: BLACK[100],
    fontSize: '16px',
    fontFamily: PRIMARY_FONT[400],
  },
  statusText: {
    color: BLACK[70],
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[300],
  },
  eligibleText: {
    color: FEED_BACK.GREEN_70,
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[700],
    fontWeight: 'bold',
  },
  notEligibleText: {
    color: FEED_BACK.RED_50,
    fontSize: '14px',
    fontFamily: PRIMARY_FONT[700],
    fontWeight: 'bold',
  },
};

const TaxCreditsCard = ({ taxCreditOpportunities, selectedUserId }) => {
  const dispatch = useDispatch();
  const isProcessingWotcEligibility = useSelector(selectIsProcessingWotcEligibility);
  const isProcessingUrbanYouthCertified = useSelector(selectIsProcessingUrbanYouthCertified);
  const isLoadingUserHistory = useSelector(selectIsLoadingUserHistory);

  const isWotcEligible = !!taxCreditOpportunities?.isWotcEligible;
  const isNyuytcEligible = !!taxCreditOpportunities?.isNyuytcEligible;
  const isUrbanYouthCertified = !!taxCreditOpportunities?.isUrbanYouthCertified;

  const handleWotcChange = useCallback(
    (event) => {
      dispatch(
        updateWotcEligibility({
          userId: selectedUserId,
          isWotcEligible: event.target.checked,
        })
      );
    },
    [dispatch, selectedUserId]
  );

  const handleUrbanYouthCertifiedChange = useCallback(
    (event) => {
      dispatch(
        updateUrbanYouthCertified({
          userId: selectedUserId,
          isUrbanYouthCertified: event.target.checked,
        })
      );
    },
    [dispatch, selectedUserId]
  );

  return (
    <SectionCard
      header={LanguageConverterV2('userDetail.taxCredits.header')}
      headerTestId="tax-credits-card-header"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <CheckBox
          onChange={handleWotcChange}
          checked={isWotcEligible}
          disabled={isProcessingWotcEligibility || isLoadingUserHistory}
          label={LanguageConverterV2('userDetail.taxCredits.wotcEligible')}
          name="wotcEligible"
          variant="standard"
          fullWidth
          sx={{
            '& .MuiFormControlLabel-label': styles.checkBoxLabel,
          }}
        />
        <Box sx={{ mt: 1 }}>
          <Typography sx={styles.checkBoxLabel}>
            {LanguageConverterV2('userDetail.taxCredits.urbanYouthTaxCredit')}
          </Typography>
          <Typography component="span" sx={styles.statusText}>
            {LanguageConverterV2('userDetail.taxCredits.status')}{' '}
            <Typography
              component="span"
              sx={isNyuytcEligible ? styles.eligibleText : styles.notEligibleText}
            >
              {isNyuytcEligible
                ? LanguageConverterV2('userDetail.taxCredits.eligible')
                : LanguageConverterV2('userDetail.taxCredits.notEligible')}
            </Typography>
          </Typography>
        </Box>
        <CheckBox
          onChange={handleUrbanYouthCertifiedChange}
          checked={isUrbanYouthCertified}
          disabled={isProcessingUrbanYouthCertified || isLoadingUserHistory}
          label={LanguageConverterV2('userDetail.taxCredits.urbanYouthCertified')}
          name="urbanYouthCertified"
          variant="standard"
          fullWidth
          sx={{
            '& .MuiFormControlLabel-label': styles.checkBoxLabel,
          }}
        />
      </Box>
    </SectionCard>
  );
};

TaxCreditsCard.propTypes = {
  taxCreditOpportunities: PropTypes.shape({
    isWotcEligible: PropTypes.bool,
    isNyuytcEligible: PropTypes.bool,
    isUrbanYouthCertified: PropTypes.bool,
  }),
  selectedUserId: PropTypes.string,
};

export default TaxCreditsCard;
